import _defineProperty from '@babel/runtime-corejs3/helpers/esm/defineProperty';
import 'core-js/modules/es.promise.js';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _bindInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/bind';
import * as React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import styled from 'styled-components';
import { selectFromTheme } from '@thisisbud/sc-utils';
import { FocusTrap } from '@thisisbud/bui-react-focus-trap';
import { Scrollable } from '@thisisbud/bui-react-scrollable';
import { H4, Paragraph } from '@thisisbud/bui-react-typography';
import { IconButton } from '@thisisbud/bui-react-buttons';
import { CloseIcon } from '@thisisbud/bui-react-icons';
import { jsx, jsxs } from 'react/jsx-runtime';

let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5,
  _t6;
const dialogClassName = 'bui-dialog';

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledDialog = /*#__PURE__*/styled.dialog.withConfig({
  displayName: "StyledDialog",
  componentId: "sc-kakpp5-0"
})(_t || (_t = _`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  border: 0;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 900;

  &::backdrop {
    display: none;
  }
`));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledFocusTrap = /*#__PURE__*/styled(FocusTrap).withConfig({
  displayName: "StyledFocusTrap",
  componentId: "sc-kakpp5-1"
})(_t2 || (_t2 = _`
  display: flex;
  width: 100%;
  height: 100%;
`));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledScrollable = /*#__PURE__*/styled(Scrollable).withConfig({
  displayName: "StyledScrollable",
  componentId: "sc-kakpp5-2"
})(_t3 || (_t3 = _`
  display: block;
  width: 100%;
  height: 100%;
`));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledPadder = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledPadder",
  componentId: "sc-kakpp5-3"
})(_t4 || (_t4 = _`
  position: relative;
  display: flex;
  width: 100%;
  min-height: 100%;
  justify-content: center;
  align-items: center;
  padding: ${0};
  box-sizing: border-box;
`), selectFromTheme('paddingDialogDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledDialogBox = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledDialogBox",
  componentId: "sc-kakpp5-4"
})(_t5 || (_t5 = _`
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: ${0};
  border-radius: ${0};
  box-shadow: ${0};
  padding: ${0};
`), selectFromTheme('colorBackgroundDialog'), selectFromTheme('borderRadiusDialogDefault'), selectFromTheme('boxShadowDialogDefault'), selectFromTheme('paddingDialogDefault'));

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledCloseButton = /*#__PURE__*/styled(IconButton).withConfig({
  displayName: "StyledCloseButton",
  componentId: "sc-kakpp5-5"
})(_t6 || (_t6 = _`
  position: absolute;
  right: ${0};
  top: ${0};
`), selectFromTheme('spacingXSmallDefault'), selectFromTheme('spacingXSmallDefault'));

/**
 * Check if a node is a Paragraph component or a `p` tag.
 *
 * @param node - The node to validate.
 * @returns - A boolean indicating whether the node is a Paragraph component or a `p` tag.
 */
function isParagraphNode(node) {
  const {
    props: {
      as
    },
    type
  } = node;

  // TODO: accept a `describedBy` prop instead of trying to detect a Paragraph component
  return type === Paragraph || type === 'p' || as === Paragraph || as === 'p';
}

/**
 * Returns the children with the appropriate props.
 *
 * @param children - The children of the dialog component.
 * @param dialogDescriptionId - The Id of the description dialog.
 * @returns - The children modified if it is the first child and is a paragraph.
 */
function getDialogChildren(children, dialogDescriptionId) {
  var _context;
  return _mapInstanceProperty(_context = React.Children.toArray(children) // Converting the children to an array filters out empty children such as `null` and `undefined`
  ).call(_context, function (child, index) {
    const {
      id
    } = child.props;
    if (index === 0 && typeof id !== 'string' && isParagraphNode(child)) {
      return /*#__PURE__*/React.cloneElement(child, {
        id: dialogDescriptionId
      });
    }
    return child;
  });
}

/**
 * Returns the description node.
 *
 * @param children - The children of the dialog component.
 * @returns - The first child if it is a paragraph node.
 */
function getDescriptionNode(children) {
  const firstChild = React.Children.toArray(children)[0];
  if (typeof firstChild !== 'undefined' && isParagraphNode(firstChild)) {
    return firstChild;
  }
}

/**
 * Returns the description id of the paragraph element.
 *
 * @param dialogId - The dialog Id.
 * @param node - The node which needs to be checked.
 * @returns - The description Id of the first paragraph or a fallback ID otherwise
 */
function getDescriptionId(dialogId, node) {
  if (typeof node !== 'undefined') {
    const {
      id
    } = node.props;
    if (typeof id === 'string') {
      return id;
    }
  }
  return `${dialogId}-description`;
}

/**
 * A dialog component to alert or require an action from the user.
 *
 * *The open state of the dialog needs to be controlled by the parent component, the `onClose` prop simply exists to inform that the user is requesting the dialog to be closed.*
 *
 * *The component will not render in a server side rendering context.*
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { Dialog } from '@thisisbud/bui-react-dialog'
 * import { Paragraph } from '@thisisbud/bui-react-typography'
 *
 * function Example() {
 *   return (
 *     <Dialog allowClose={false} id='example' open={true} title='This is Bud'>
 *       <Paragraph>Unlock the power of enriched financial data to drive growth</Paragraph>
 *     </Form>
 *   )
 * }
 * ```
 */
class Dialog extends React.Component {
  constructor(props) {
    var _context2;
    super(props);
    _defineProperty(this, "dialogRef", void 0);
    _defineProperty(this, "previouslyFocusedEl", null);
    this.dialogRef = /*#__PURE__*/React.createRef();
    this.onEscKeyPress = _bindInstanceProperty(_context2 = this.onEscKeyPress).call(_context2, this);
  }

  /**
   * Handle initial set up of the dialog.
   */
  componentDidMount() {
    const {
      open
    } = this.props;
    if (open === true) {
      this.initDialog();
    }
  }

  /**
   * Handle updates to the open state of the dialog.
   *
   * @param prevProps - The previous component props
   */
  componentDidUpdate(prevProps) {
    const {
      open
    } = this.props;
    const {
      open: prevOpen
    } = prevProps;
    if (Boolean(open) === Boolean(prevOpen)) {
      return;
    }
    if (open === true) {
      this.initDialog();
    } else {
      this.destroyDialog();
    }
  }

  /**
   * Handle teardown of the dialog window before unmounting.
   */
  componentWillUnmount() {
    const {
      open
    } = this.props;
    if (open === true) {
      this.destroyDialog();
    }
  }

  /**
   * Reference to the dialog element.
   */

  /**
   * Handle Esc key presses to close the dialog.
   *
   * @param e - The keyboard event object
   */
  onEscKeyPress(e) {
    if (e.key !== 'Escape') {
      return;
    }
    const focusedElement = document.activeElement;
    const dialog = this.dialogRef.current;

    // Check if the key press was in the active dialog window
    if ((focusedElement === null || focusedElement === void 0 ? void 0 : focusedElement.contains(dialog)) !== true && (dialog === null || dialog === void 0 ? void 0 : dialog.contains(focusedElement)) !== true) {
      return;
    }

    // @ts-expect-error the onClose prop is only availble when allowClose is true
    const {
      allowClose,
      onClose
    } = this.props;
    if (allowClose !== true) {
      e.preventDefault();
      return;
    }
    onClose(e);
  }

  /**
   * Initialize the dialog window.
   */
  initDialog() {
    window.addEventListener('keyup', this.onEscKeyPress);
  }

  /**
   * Destroy the dialog window
   */
  destroyDialog() {
    window.removeEventListener('keyup', this.onEscKeyPress);
  }

  /**
   * Render the component.
   */
  render() {
    const {
      allowClose,
      children,
      className,
      // @ts-expect-error the closeLabel prop is only available when allowClose is true
      closeLabel,
      id,
      // @ts-expect-error the labelledBy prop can be used instead of title
      labelledBy,
      // @ts-expect-error the onClose prop is only available when allowClose is true
      onClose,
      open,
      // @ts-expect-error the title prop can be used instead of labelledBy
      title
    } = this.props;
    if (open !== true || typeof window === 'undefined') {
      return null;
    }
    const descriptionNode = getDescriptionNode(children);
    const dialogDescriptionId = getDescriptionId(id, descriptionNode);
    const titleId = typeof title !== 'undefined' ? `${id}-title` : labelledBy;
    return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/jsx(StyledDialog, {
      "aria-describedby": descriptionNode ? dialogDescriptionId : undefined,
      "aria-labelledby": titleId,
      "aria-modal": true,
      className: classnames(dialogClassName, className),
      id: id,
      open: true,
      ref: this.dialogRef,
      role: "dialog",
      children: /*#__PURE__*/jsx(StyledFocusTrap, {
        children: /*#__PURE__*/jsx(StyledScrollable, {
          lockOuterScroll: true,
          verticalScroll: true,
          children: /*#__PURE__*/jsx(StyledPadder, {
            children: /*#__PURE__*/jsxs(StyledDialogBox, {
              children: [typeof title !== 'undefined' ? /*#__PURE__*/jsx(H4, {
                as: "h1",
                id: titleId,
                children: title
              }) : null, getDialogChildren(children, dialogDescriptionId), allowClose === true ? /*#__PURE__*/jsx(StyledCloseButton, {
                onClick: onClose,
                children: /*#__PURE__*/jsx(CloseIcon, {
                  label: closeLabel
                })
              }) : null]
            })
          })
        })
      })
    }), document.body);
  }
}
_defineProperty(Dialog, "displayName", 'Dialog');
_defineProperty(Dialog, "defaultProps", {
  open: true
});

export { Dialog, dialogClassName };
